import { lazy, Suspense, useEffect, useState } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
} from "react-router-dom";
import "./i18n";

import RootLayout from "./components/RootLayout/RootLayout";
import LoadingOverlay from "./components/UI/LoadingOverlay/LoadingOverlay";
import HomePage from "./pages/home/HomePage";
import { initGtag } from "./analytics/gtag";
import { getLangFromPathname } from "./utils/langRouting";

const ContactPage = lazy(() => import("./pages/contact/ContactPage"));
const PortfolioPage = lazy(() => import("./pages/portfolio/PortfolioPage"));
const ServicesPage = lazy(() => import("./pages/services/ServicesPage"));
const ServiceDetailPage = lazy(() => import("./pages/services/ServiceDetailPage"));
const NotFoundPage = lazy(() => import("./pages/not-found/NotFoundPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/privacy/PrivacyPolicyPage"));
const TermsAndConditionsPage = lazy(
  () => import("./pages/terms/TermsAndConditionsPage"),
);

function TermsAliasRedirect() {
  const { pathname } = useLocation();
  const lang = getLangFromPathname(pathname);
  const prefix = lang === "en" ? "/en" : "";
  return <Navigate to={`${prefix}/terms-and-conditions`} replace />;
}

const marketingChildren = [
  { path: "", index: true, element: <HomePage /> },
  { path: "portfolio", element: <Suspense fallback={null}><PortfolioPage /></Suspense> },
  { path: "contact", element: <Suspense fallback={null}><ContactPage /></Suspense> },
  { path: "services/:serviceSlug", element: <Suspense fallback={null}><ServiceDetailPage /></Suspense> },
  { path: "services", element: <Suspense fallback={null}><ServicesPage /></Suspense> },
  { path: "privacy-policy", element: <Suspense fallback={null}><PrivacyPolicyPage /></Suspense> },
  { path: "privacy-policy/:gameSlug", element: <Suspense fallback={null}><PrivacyPolicyPage /></Suspense> },
  { path: "terms", element: <TermsAliasRedirect /> },
  {
    path: "terms-and-conditions",
    element: (
      <Suspense fallback={null}>
        <TermsAndConditionsPage />
      </Suspense>
    ),
  },
  {
    path: "terms-and-conditions/:gameSlug",
    element: (
      <Suspense fallback={null}>
        <TermsAndConditionsPage />
      </Suspense>
    ),
  },
  { path: "*", element: <Suspense fallback={null}><NotFoundPage /></Suspense> },
];

const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <NotFoundPage />,
    children: marketingChildren,
  },
  {
    path: "/en",
    element: <RootLayout />,
    errorElement: <NotFoundPage />,
    children: marketingChildren,
  },
]);

function App() {
  const [isInitialLoading, setIsInitialLoading] = useState(
    () => document.getElementById("root")?.dataset?.prerendered !== "true"
  );

  useEffect(() => {
    initGtag();
  }, []);

  const handleLoadingComplete = () => {
    setIsInitialLoading(false);
  };

  return (
    <>
      <LoadingOverlay
        isVisible={isInitialLoading}
        onComplete={handleLoadingComplete}
      />
      <RouterProvider router={router} />
    </>
  );
}

export default App;
